import { styled } from '@mui/material/styles';
import Avatar from 'components/common/avatar/Avatar';
import AddPostModal from 'components/modals/addPostModal/AddPostModal';
import { USER_PROFILE } from 'constants/routes';
import useUserData from 'hooks/useUserData';
import { Fragment, ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button as MuiButton, Typography } from '@mui/material';
import { ArticleSourceType, ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { useLocation } from 'react-router-dom';
import QuestionFillIcon from 'remixicon-react/QuestionFillIcon';
import { theme } from 'config/theme';
import { HomeModuleFeatures, useBuildWiseModules } from '@buildwise/libs/helpers';
import FileListFillIcon from 'remixicon-react/FileListFillIcon';
import ChatQuoteFillIcon from 'remixicon-react/ChatQuoteFillIcon';
import { UserLink } from '@buildwise/pages/knowledge/components/DraftStatusMessage';
import { ArticleCreationMethods } from '@buildwise/libs/posts';
import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';

export type ArticleActionButton = {
  button: ReactNode;
  name: string;
};

type LongAddPostButtonProps = {
  defaultPrompt?: (() => string) | string;
  disabled?: boolean;
  formId?: string;
  articleFormState?: PostFormData;
  projectId?: string;
  helperText?: string;
  getActions?: (options: { actions: ArticleActionButton[] }) => ArticleActionButton[];
  onAddArticleCallback?: (options: { article: ArticleType }) => void;
};

function LongAddPostButton({
  defaultPrompt = '',
  disabled = false,
  formId,
  articleFormState,
  projectId,
  helperText,
  getActions,
  onAddArticleCallback,
}: LongAddPostButtonProps) {
  const { state } = useLocation();
  const { user } = useUserData();
  const [articleModalState, setArticleModalState] = useState<{
    open: boolean;
    articleType: ArticleSourceType;
    postCreationMethod: ArticleCreationMethods;
  }>({
    open: !!state?.articleFormState,
    articleType: state?.articleFormState?.articleType || ArticleSourceType.POST,
    postCreationMethod: ArticleCreationMethods.WRITE_MANUALLY,
  });
  const { isLoadingBuildWiseModules, homeModuleFeatures } = useBuildWiseModules();
  const actions = useMemo(() => {
    const actions: ArticleActionButton[] = [
      {
        button: (
          <Button
            disabled={disabled}
            startIcon={<FileListFillIcon size={20} />}
            onClick={() =>
              setArticleModalState((prevState) => ({
                ...prevState,
                open: true,
                articleType: ArticleSourceType.POST,
                postCreationMethod: ArticleCreationMethods.WRITE_MANUALLY,
              }))
            }
            variant="contained"
            sx={(theme) => ({
              color: theme.palette.custom.tuna,
              fontSize: 15,
              whiteSpace: 'nowrap',
              fontWeight: 700,
              backgroundColor: theme.palette.custom.paleGold,
              '&:hover': {
                backgroundColor: theme.palette.custom.paleGold,
              },
              '& svg': {
                color: disabled ? 'inherit' : theme.palette.custom.pirateGold,
              },
            })}
          >
            {homeModuleFeatures['Share Knowledge'].displayName}
          </Button>
        ),
        name: HomeModuleFeatures.SHARE_KNOWLEDGE,
      },
    ];

    if (homeModuleFeatures[HomeModuleFeatures.ASK_PEERS].isActive) {
      actions.push({
        button: (
          <Button
            disabled={disabled}
            startIcon={<QuestionFillIcon size={20} />}
            onClick={() =>
              setArticleModalState((prevState) => ({
                ...prevState,
                open: true,
                articleType: ArticleSourceType.QUESTION,
                postCreationMethod: ArticleCreationMethods.WRITE_MANUALLY,
              }))
            }
            variant="contained"
            sx={{
              color: theme.palette.custom.tuna,
              border: 'none',
              fontSize: 15,
              whiteSpace: 'nowrap',
              fontWeight: 700,
              backgroundColor: theme.palette.custom.lightBlue,
              '&:hover': {
                border: 'none',
                backgroundColor: theme.palette.custom.lightBlue,
              },
              '& svg': {
                color: disabled ? 'inherit' : theme.palette.custom.dodgerBlue,
              },
            }}
          >
            {homeModuleFeatures[HomeModuleFeatures.ASK_PEERS].displayName}
          </Button>
        ),
        name: HomeModuleFeatures.ASK_PEERS,
      });
    }

    if (homeModuleFeatures[HomeModuleFeatures.SUGGEST_IMPROVEMENTS].isActive) {
      actions.push({
        button: (
          <Button
            disabled={disabled}
            startIcon={<ChatQuoteFillIcon size={20} />}
            onClick={() =>
              setArticleModalState((prevState) => ({
                ...prevState,
                open: true,
                articleType: ArticleSourceType.SUGGESTION,
                postCreationMethod: ArticleCreationMethods.WRITE_MANUALLY,
              }))
            }
            variant="contained"
            sx={{
              color: theme.palette.custom.tuna,
              backgroundColor: theme.palette.custom.chinook,
              '&:hover': {
                backgroundColor: theme.palette.custom.chinook,
              },
              '& svg': {
                color: disabled ? 'inherit' : theme.palette.custom.cloverGreen,
              },
            }}
          >
            {homeModuleFeatures[HomeModuleFeatures.SUGGEST_IMPROVEMENTS].displayName}
          </Button>
        ),
        name: HomeModuleFeatures.SUGGEST_IMPROVEMENTS,
      });
    }

    if (homeModuleFeatures[HomeModuleFeatures.SUMMARIZE_FORM_WITH_AI].isActive) {
      actions.push({
        button: (
          <Button
            disabled={disabled}
            variant="text"
            sx={(theme) => ({
              backgroundColor: theme.palette.custom.white,
              mr: 1,
            })}
            onClick={() =>
              setArticleModalState((prevState) => ({
                ...prevState,
                open: true,
                articleType: ArticleSourceType.POST,
                postCreationMethod: ArticleCreationMethods.WRITE_WITH_AI,
              }))
            }
          >
            {homeModuleFeatures[HomeModuleFeatures.SUMMARIZE_FORM_WITH_AI].displayName}
          </Button>
        ),
        name: HomeModuleFeatures.SUMMARIZE_FORM_WITH_AI,
      });
    }

    if (homeModuleFeatures[HomeModuleFeatures.MAKE_A_POST].isActive) {
      actions.push({
        button: (
          <Button
            disabled={disabled}
            variant="outlined"
            sx={(theme) => ({
              backgroundColor: theme.palette.custom.white,
              mr: 1,
              border: '1px solid black',
            })}
            onClick={() =>
              setArticleModalState((prevState) => ({
                ...prevState,
                open: true,
                articleType: ArticleSourceType.POST,
              }))
            }
          >
            {homeModuleFeatures[HomeModuleFeatures.MAKE_A_POST].displayName}
          </Button>
        ),
        name: HomeModuleFeatures.SUMMARIZE_FORM_WITH_AI,
      });
    }

    return getActions?.({ actions }) || actions;
  }, [disabled, homeModuleFeatures, getActions]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showAvatar, setShowAvatar] = useState(true);
  const [showUserName, setShowUserName] = useState(true);

  useLayoutEffect(() => {
    const updateLayout = () => {
      if (containerRef.current) {
        const children = containerRef.current.children;
        if (children.length >= 2) {
          const firstChild = children[0].getBoundingClientRect();
          const lastChild = children[children.length - 1].getBoundingClientRect();

          const gap = lastChild.left - firstChild.right;
          if (gap < 16) {
            setShowUserName(false);

            if (containerRef.current.clientWidth < containerRef.current.scrollWidth) {
              setShowAvatar(false);
            } else {
              setShowAvatar(true);
            }
          } else {
            setShowAvatar(true);
            setShowUserName(true);
          }
        }
      }
    };

    updateLayout();

    window.addEventListener('resize', updateLayout);

    return () => {
      window.removeEventListener('resize', updateLayout);
    };
  }, [containerRef.current, isLoadingBuildWiseModules, homeModuleFeatures]);

  if (isLoadingBuildWiseModules) {
    return null;
  }

  return (
    <>
      <ButtonsContainer
        ref={containerRef}
        sx={{ justifyContent: showAvatar ? 'space-between' : 'center' }}
      >
        <Box display="flex" alignItems="center">
          {showAvatar && !helperText && (
            <>
              <Avatar
                src={user?.profileImageURL}
                size={38}
                name={user?.userNameToDisplay}
                to={USER_PROFILE.replace(':userName', user?.username || '')}
              />
              {showUserName && (
                <Box ml={1}>
                  <UserLink
                    mail={user?.username || ''}
                    name={user?.userNameToDisplay || ''}
                    sx={{
                      lineHeight: 1.2,
                      display: 'block',
                      textTransform: 'capitalize',
                      whiteSpace: 'nowrap',
                      width: 'fit-content',
                    }}
                  />
                </Box>
              )}
            </>
          )}
          {helperText && (
            <Typography sx={{ color: theme.palette.custom.tuna, fontWeight: 700, fontSize: 15 }}>
              {helperText}
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          {actions.map((action, index) => (
            <Fragment key={index}> {action.button}</Fragment>
          ))}
        </Box>
      </ButtonsContainer>

      {articleModalState.open && (
        <AddPostModal
          articleType={articleModalState.articleType}
          open={articleModalState.open}
          setOpen={() => {
            window.scrollTo(0, 0);
            setArticleModalState((prevState) => ({
              ...prevState,
              open: false,
              articleType: ArticleSourceType.POST,
              postCreationMethod: ArticleCreationMethods.WRITE_MANUALLY,
            }));
          }}
          selectedFeedFilters={[]}
          {...(onAddArticleCallback && { onAddArticleCallback })}
          {...(formId && { formIds: [formId] })}
          articleFormState={state?.articleFormState || articleFormState}
          postCreationMethod={articleModalState.postCreationMethod}
          defaultPrompt={typeof defaultPrompt === 'string' ? defaultPrompt : defaultPrompt?.()}
          defaultProjectId={projectId}
        />
      )}
    </>
  );
}

const ButtonsContainer = styled(Box, { label: 'ButtonsContainer' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  backgroundColor: theme.palette.custom.white,
  padding: '12px 16px',
  borderRadius: 8,
  boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
  overflow: 'hidden',
}));

const Button = styled(MuiButton)({
  fontSize: 15,
  whiteSpace: 'nowrap',
  fontWeight: 700,
  border: 'none',
});

export default LongAddPostButton;
